// Consolidated Track page: SMS and Email delivery logs in one tabbed view.
// Tab content is provided by the existing pages rendered with embedded={true}.

import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

// Material Dashboard 2 React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import UnderlineTabs from "components/ui/UnderlineTabs";
import EmptyState from "components/ui/EmptyState";
import usePermission from "components/Utils/usePermission";

// Tab content (existing pages, rendered without their own layout shell)
import TrackSms from "layouts/track/TrackSms";
import TrackEmail from "layouts/track/TrackEmail";

// i18n
import { useTranslation } from "react-i18next";

function Track() {
  const checkPermission = usePermission();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabs = useMemo(() => {
    const list = [];
    if (checkPermission("Tracking", "Sms")) {
      list.push({ value: "sms", label: t("track_sms") });
    }
    if (checkPermission("Tracking", "Email")) {
      list.push({ value: "email", label: t("track_email") });
    }
    return list;
  }, [checkPermission, t]);

  const requested = searchParams.get("tab");
  const activeTab = tabs.some((tab) => tab.value === requested)
    ? requested
    : tabs.length > 0
    ? tabs[0].value
    : null;

  const handleChange = (event, value) => {
    setSearchParams({ tab: value }, { replace: true });
  };

  // Rendered by whichever tab is active, positioned between its own header
  // row (title/count, date range) and its filter+table card.
  const tabsSlot = <UnderlineTabs value={activeTab} onChange={handleChange} tabs={tabs} />;

  return (
    <DashboardLayout>
      <MDBox
        mt={4}
        sx={{
          display: "flex",
          flexDirection: "column",
          // Fills the viewport down to the screen edge: 64px navbar + 24px
          // DashboardLayout bottom padding + this box's own 32px top margin.
          height: "calc(100vh - 120px)",
        }}
      >
        {tabs.length === 0 ? (
          <EmptyState
            icon="lock"
            title={t("no_permission", { defaultValue: "You do not have access to Track" })}
          />
        ) : (
          <>
            {activeTab === "sms" && <TrackSms embedded tabsSlot={tabsSlot} />}
            {activeTab === "email" && <TrackEmail embedded tabsSlot={tabsSlot} />}
          </>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Track;
